import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Star } from "lucide-react";
import { api, formatZAR } from "../lib/api";
import { submitPayFast } from "../lib/payfast";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const FAQ = [
  { q: "Can I cancel anytime?", a: "Yes. Cancel from your dashboard anytime. You'll keep access until the end of your paid period." },
  { q: "Do you offer a free trial?", a: "We don't — but we offer a huge amount of free content so you can experience our quality before committing." },
  { q: "Is my payment secure?", a: "Yes. All payments are processed by Stripe. We never store your card details." },
  { q: "Can I gift a membership?", a: "Yes, contact us through the Contact page and we'll set it up." },
];

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/memberships/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const subscribe = async (tier) => {
    if (!user) {
      toast.error("Please sign in to subscribe");
      navigate("/login?next=/membership");
      return;
    }
    setBusy(tier);
    try {
      const { data } = await api.post("/payments/payfast/membership", {
        tier,
        origin_url: window.location.origin,
      });
      submitPayFast(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div>
      <section className="bg-[#0B1526] text-white py-24 sm:py-32 relative overflow-hidden" data-testid="membership-hero">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1756136836696-0e3374ebfbac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Crown className="w-12 h-12 text-[#C00000] mx-auto mb-6" />
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">Membership</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mb-6">
            Praise without <span className="italic text-[#C00000]">limits.</span>
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Exclusive videos. Early releases. Behind-the-scenes. Devotionals. Members-only discounts.
            Cancel anytime.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="membership-plans">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <div
              key={p.tier}
              className={`relative p-10 border-2 ${p.popular ? "border-[#C00000] bg-[#1F3864] text-white" : "border-gray-200 bg-white"}`}
              data-testid={`plan-${p.tier}`}
            >
              {p.popular && (
                <div className="absolute -top-4 left-10 px-3 py-1 bg-[#C00000] text-white text-[10px] tracking-[0.3em] uppercase flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> Best Value
                </div>
              )}
              <div className={`text-xs tracking-[0.3em] uppercase mb-3 ${p.popular ? "text-[#C00000]" : "text-[#C00000]"}`}>{p.name}</div>
              <div className="flex items-baseline gap-2 mb-6">
                <div className={`font-display text-5xl ${p.popular ? "" : "text-[#1F3864]"}`}>{formatZAR(p.price)}</div>
                <div className={p.popular ? "text-white/60" : "text-gray-500"}>/{p.cycle}</div>
              </div>
              {p.savings && <div className="text-sm text-[#C00000] font-semibold mb-4">{p.savings}</div>}
              <ul className="space-y-3 mb-8">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${p.popular ? "text-[#C00000]" : "text-[#1F3864]"}`} />
                    <span className={p.popular ? "text-white/90" : "text-gray-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(p.tier)}
                disabled={busy === p.tier}
                className={`w-full py-3.5 text-sm tracking-[0.15em] uppercase font-medium transition-colors disabled:opacity-60 ${
                  p.popular ? "bg-[#C00000] hover:bg-[#990000] text-white" : "bg-[#1F3864] hover:bg-[#162846] text-white"
                }`}
                data-testid={`action-subscribe-${p.tier}`}
              >
                {busy === p.tier ? "Loading..." : `Join ${p.name}`}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-20" data-testid="membership-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl text-[#1F3864] mb-10">What members are saying.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Lerato M.", quote: "The exclusive videos alone are worth it. The devotionals feed my soul every single morning." },
              { name: "Themba K.", quote: "I got early access to the latest album two weeks before release. The discount on the hoodie paid for my membership twice over." },
              { name: "Zanele D.", quote: "Worth every rand. I've not missed a single Next Gen Praise event since becoming a member." },
            ].map((t, i) => (
              <div key={i} className="bg-white p-8 border-l-4 border-[#C00000]">
                <p className="text-gray-700 italic leading-relaxed mb-4">"{t.quote}"</p>
                <div className="text-sm font-semibold text-[#1F3864]">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="membership-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl text-[#1F3864] mb-10 text-center">Questions, answered.</h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="group border border-gray-200 p-6 open:border-[#1F3864]" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-display text-lg text-[#1F3864] list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-[#C00000] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
